import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';

/**
 * Custom element for the header search drawer. Opens on button click, closes on
 * backdrop click or Escape. Traps focus and locks scroll when open.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} drawer - The drawer panel element.
 * @property {HTMLElement} backdrop - The backdrop element.
 *
 * @extends {Component<Refs>}
 */
class SearchDrawerComponent extends Component {
  requiredRefs = ['drawer', 'backdrop'];

  connectedCallback() {
    super.connectedCallback();

    this.refs.backdrop.addEventListener('click', this.closeDrawer);
    document.addEventListener('keydown', this.#onKeyDown);
    document.addEventListener('click', this.#onDocumentClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.backdrop.removeEventListener('click', this.closeDrawer);
    document.removeEventListener('keydown', this.#onKeyDown);
    document.removeEventListener('click', this.#onDocumentClick);
    removeTrapFocus();
    document.documentElement.removeAttribute('scroll-lock');
  }

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (!this.isOpen) return;
    const header = document.getElementById('header-component');
    if (header && !header.contains(/** @type {Node} */ (event.target))) {
      this.closeDrawer();
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (!this.isOpen) return;
    if (event.key !== 'Escape') return;
    this.closeDrawer();
  };

  get isOpen() {
    return this.hasAttribute('open');
  }

  toggleDrawer() {
    if (this.isOpen) {
      this.closeDrawer();
    } else {
      this.showDrawer();
    }
  }

  /** @param {boolean} expanded */
  #updateTriggerButton(expanded) {
    /** @type {HTMLButtonElement | null} */
    const trigger = document.querySelector(`[aria-controls="${this.id}"]`);
    if (trigger) {
      trigger.setAttribute('aria-expanded', String(expanded));
      
      if (!expanded) {
        setTimeout(() => trigger.focus(), 0);
      }
    }
  }

  showDrawer() {
    if (this.isOpen) return;

    document.dispatchEvent(
      new CustomEvent('header:drawer-open', { detail: { source: 'search-drawer' }, bubbles: true })
    );

    this.setAttribute('open', '');
    this.#updateTriggerButton(true);

    document.documentElement.setAttribute('scroll-lock', '');

    requestAnimationFrame(() => {
      const firstFocusable = this.refs.drawer.querySelector(
        'input:not([type="hidden"]), button, [href], [tabindex]:not([tabindex="-1"])'
      );

      if (firstFocusable instanceof HTMLElement) {
        setTimeout(() => firstFocusable.focus(), 0);
      }

      trapFocus(this.refs.drawer);
    });
  }

  closeDrawer = () => {
    if (!this.isOpen) return;

    this.#updateTriggerButton(false);
    removeTrapFocus();
    document.documentElement.removeAttribute('scroll-lock');
    this.removeAttribute('open');
  };
}

if (!customElements.get('search-drawer')) {
  customElements.define('search-drawer', SearchDrawerComponent);
}
